import { useSelectorApp, useDispatchApp } from "../../lib/redux";
import { useNotifications } from "../../hooks/useNotifications";
import { NotificationsPanel } from "./NotificationsPanel";
import {
  setNotificationsPanelIsExpanded,
  setNotificationsPanelIsClosed,
} from "../../actions/ui";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import {
  getOpenAlertModals,
  closeAlertModal,
} from "../../utils/alertModalsState";
import useOutsideCampaignGallery from "../../hooks/useOutsideCampaignGallery";
import ModalCreateWithIA from "../Modal/ModalCreateWithIA";
import ModalCustomReport from "../Modal/ModalCustomReport";
import ModalGallery from "../Modal/ModalGallery";
import ModalImageEdit from "../Modal/ModalImageEdit";
import { hasAlertsChanged } from "../../utils/notifications";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startAddContentsToList,
  startBumpAndUpdateContentInList,
  startBumpContentInList,
  startUpdateCarouselImageUrl,
} from "../../actions/user";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ALERTS_ASYNC_STATUS,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import { MODAL_CREATE_WITH_IA_YOU_CAN_LEAVE_THE_PAGE } from "../../i18n/keysTranslations";

const GlobalNotificationsPanel = () => {
  const { t } = useTranslationApp();
  const isMobileViewport = useMediaQuery((theme) =>
    theme.breakpoints.down("md")
  );
  const dispatch = useDispatchApp();
  const notificationsPanelIsExpanded = useSelectorApp(
    (state) => state.alerts?.notificationsPanelIsExpanded || false
  );
  const notificationsPanelIsClosed = useSelectorApp(
    (state) => state.alerts?.notificationsPanelIsClosed || false
  );
  const { activeAlerts, onCloseAlert } = useNotifications();
  const [openModals, setOpenModals] = useState([]);
  const previousAlertsRef = useRef([]);
  const panelRef = useRef(null);
  const wasManuallyClosedRef = useRef(false);

  const { userID } = dispatch(getPathDatabaseByRole());
  const { onOpenGallery, galleryModal } = useOutsideCampaignGallery();

  const isModalGalleryOpen = useSelectorApp(
    (state) => state.ui?.openModals?.isModalGallery || false
  );
  const isModalCreateWithIAOpen = useSelectorApp(
    (state) => state.ui?.openModals?.isModalCreateWithIA || false
  );

  const areModalsClosed = !isModalGalleryOpen && !isModalCreateWithIAOpen;

  const hasGeneratingCreativeInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "generatingCreative" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const hasCustomReportInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "generatingCustomReport" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const hasAnalyzingTemplateInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "analyzingCreativeTemplate" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const hasProducingTemplateInProgress = activeAlerts.some(
    (alert) =>
      alert?.type === "producingCreativeTemplate" &&
      (alert?.status === ALERTS_ASYNC_STATUS.LOADING ||
        alert?.status === ALERTS_ASYNC_STATUS.GENERATING)
  );
  const headerHint =
    (isModalCreateWithIAOpen && hasGeneratingCreativeInProgress) ||
    hasCustomReportInProgress ||
    hasAnalyzingTemplateInProgress ||
    hasProducingTemplateInProgress
      ? t(MODAL_CREATE_WITH_IA_YOU_CAN_LEAVE_THE_PAGE)
      : null;

  // Track when user manually closes panel
  useLayoutEffect(() => {
    if (notificationsPanelIsClosed && !notificationsPanelIsExpanded) {
      if (!wasManuallyClosedRef.current) {
        wasManuallyClosedRef.current = true;
      }
    } else if (notificationsPanelIsExpanded) {
      wasManuallyClosedRef.current = false;
    }
  }, [notificationsPanelIsClosed, notificationsPanelIsExpanded]);

  useEffect(() => {
    const previousAlerts = previousAlertsRef.current;

    // Handle first alert arrival - auto-open (desktop/tablet only; mobile uses manual toggle)
    if (previousAlerts.length === 0 && activeAlerts.length > 0) {
      previousAlertsRef.current = activeAlerts.map((alert) => ({ ...alert }));
      if (!notificationsPanelIsExpanded && !isMobileViewport) {
        dispatch(setNotificationsPanelIsExpanded(true));
        dispatch(setNotificationsPanelIsClosed(false));
        wasManuallyClosedRef.current = false;
      }
      return;
    }

    // Check if alerts have changed (new alerts or status changes)
    const alertsChanged = hasAlertsChanged(activeAlerts, previousAlerts);

    // If panel is already open, keep it open to prevent flickering when alert status changes
    if (notificationsPanelIsExpanded) {
      previousAlertsRef.current = activeAlerts.map((alert) => ({ ...alert }));
      if (wasManuallyClosedRef.current) {
        wasManuallyClosedRef.current = false;
      }
      return;
    }

    // If user manually closed panel, auto-open only if alerts changed
    if (wasManuallyClosedRef.current && !notificationsPanelIsExpanded) {
      if (alertsChanged && !isMobileViewport) {
        dispatch(setNotificationsPanelIsExpanded(true));
        dispatch(setNotificationsPanelIsClosed(false));
        wasManuallyClosedRef.current = false;
        previousAlertsRef.current = activeAlerts.map((alert) => ({ ...alert }));
      }
      // Don't update previousAlertsRef if panel stays closed - we need to keep the old state for comparison
      // This allows us to detect changes when alerts update later
      return;
    }

    // Auto-open panel if alerts changed and panel is not expanded (skipped on mobile)
    if (alertsChanged && !notificationsPanelIsExpanded && !isMobileViewport) {
      dispatch(setNotificationsPanelIsExpanded(true));
      dispatch(setNotificationsPanelIsClosed(false));
      previousAlertsRef.current = activeAlerts.map((alert) => ({ ...alert }));
    }
  }, [activeAlerts, notificationsPanelIsExpanded, dispatch, isMobileViewport]);

  const onClose = useCallback(() => {
    wasManuallyClosedRef.current = true;
    previousAlertsRef.current = activeAlerts.map((alert) => ({ ...alert }));
    dispatch(setNotificationsPanelIsExpanded(false));
    dispatch(setNotificationsPanelIsClosed(true));
  }, [dispatch, activeAlerts]);

  useEffect(() => {
    const onModalStateChange = () => {
      setOpenModals(getOpenAlertModals());
    };

    window.addEventListener("alertModalStateChanged", onModalStateChange);
    onModalStateChange();

    return () => {
      window.removeEventListener("alertModalStateChanged", onModalStateChange);
    };
  }, []);

  const onCloseModal = (alertId, originalOnClose) => {
    closeAlertModal(alertId);
    if (originalOnClose) {
      originalOnClose();
    }
  };

  const onSaveImageEditFromAlert = useCallback(
    async (savePayload, generationData) => {
      const editedUrl =
        typeof savePayload === "string" ? savePayload : savePayload?.url;
      const mode =
        typeof savePayload === "string"
          ? "replace"
          : savePayload?.mode || "replace";
      if (!editedUrl) return;

      if (mode === "new") {
        await dispatch(
          startAddContentsToList([
            { url: editedUrl, from: "ai", userID: null },
          ])
        );
        return;
      }

      const contentID = generationData?.contentID;
      if (!contentID) return;

      if (
        generationData?.isCarousel &&
        generationData?.carouselIndex !== undefined &&
        generationData?.carouselIndex !== null
      ) {
        await dispatch(
          startUpdateCarouselImageUrl({
            contentID,
            carouselIndex: generationData.carouselIndex,
            url: editedUrl,
          })
        );
        await dispatch(startBumpContentInList({ id: contentID }));
        return;
      }

      await dispatch(
        startBumpAndUpdateContentInList({ id: contentID, url: editedUrl })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    const onClickOutside = (event) => {
      if (
        notificationsPanelIsExpanded &&
        panelRef.current &&
        !panelRef.current.contains(event.target)
      ) {
        const notificationsButton = document.getElementById(
          "notifications-toggle-button"
        );
        const isClickOnButton =
          notificationsButton &&
          (notificationsButton.contains(event.target) ||
            notificationsButton === event.target ||
            event.target.closest("#notifications-toggle-button"));

        if (!isClickOnButton) {
          onClose();
        }
      }
    };

    if (notificationsPanelIsExpanded) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("click", onClickOutside, { capture: true });
      }, 50);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("click", onClickOutside, {
          capture: true,
        });
      };
    }
  }, [notificationsPanelIsExpanded, onClose]);

  return (
    <>
      <Box ref={panelRef}>
        <NotificationsPanel
          isExpanded={notificationsPanelIsExpanded}
          activeAlerts={activeAlerts}
          onClose={onClose}
          onCloseAlert={onCloseAlert}
          zIndex={2000}
          areModalsClosed={areModalsClosed}
          headerHint={headerHint}
        />
      </Box>
      {openModals.map(
        ({ alertId, generationData, onClose: originalOnClose }) => {
          // Stale snapshot: openAlertModal stores generationData at click time.
          // Redux updates the same alert when generation finishes; merge live data
          // so ModalCreateWithIA receives isGenerating: false and new generatedMedia.
          const liveAlert = activeAlerts.find((a) => a.id === alertId);
          const effectiveGenerationData =
            liveAlert?.generationData ?? generationData;

          const modalType =
            effectiveGenerationData?.modalType || "createWithIA";

          if (modalType === "customReport") {
            return (
              <ModalCustomReport
                key={alertId}
                modalOpen={true}
                onCloseModal={() => onCloseModal(alertId, originalOnClose)}
                campaignID={effectiveGenerationData?.campaignID || ""}
                generationData={effectiveGenerationData}
                jobID={liveAlert?.jobID || alertId}
                alertStatus={liveAlert?.status}
                alertMessage={liveAlert?.message}
              />
            );
          }

          if (modalType === "gallery") {
            return (
              <ModalGallery
                key={alertId}
                modalOpen={true}
                onCloseModal={() => onCloseModal(alertId, originalOnClose)}
                multiple={true}
                isOutsideCampaign={true}
                acceptedFormats={IMAGE_FORMATS.concat(VIDEO_FORMATS).concat(
                  ADS_CAMPAIGN_CAROUSEL_FORMAT
                )}
                pathStorage={`socialMedia/${userID}/`}
                modeUpload="sync"
                onCallbackSelectedItems={() => {}}
                closeActionsDisabled={true}
                enableAnimationAlertPlaceholder={true}
              />
            );
          }

          if (modalType === "imageEditLayers") {
            return (
              <ModalImageEdit
                key={alertId}
                isOpen={true}
                onClose={() => onCloseModal(alertId, originalOnClose)}
                initialImageUrl={
                  effectiveGenerationData?.sourceUrl ||
                  effectiveGenerationData?.url ||
                  null
                }
                initialWorkMode="layers"
                jobID={liveAlert?.jobID || alertId}
                alertStatus={liveAlert?.status}
                generationData={effectiveGenerationData}
                onCallbackSave={(payload) =>
                  onSaveImageEditFromAlert(payload, effectiveGenerationData)
                }
              />
            );
          }

          return (
            <ModalCreateWithIA
              key={alertId}
              modalOpen={true}
              onCloseModal={() => onCloseModal(alertId, originalOnClose)}
              initialValues={effectiveGenerationData}
              onClosePreviousComponents={() =>
                onCloseModal(alertId, originalOnClose)
              }
              doReUpdateInitialValues={true}
              onCallbackAfterSaveToGallery={onOpenGallery}
            />
          );
        }
      )}
      {galleryModal}
    </>
  );
};

export default GlobalNotificationsPanel;
